import { METHOD, sendJSONRequest } from '@/state/http-request.helpers.ts';
import { buildApiUrl } from '@/utils/build-api-url';
import type { QueryClient, QueryKey } from '@tanstack/react-query';
import { GET_FLOW_QUERY_KEY } from '@/state/queries/flows/use-get-flow';
import { ServerError } from '@/types/errors/server.error';
import type { FlowDto } from '@/types/dtos/flows/response/flow.dto';

export type ApplySyncMutationBody = {
  type: 'applySync';
  baseVersion: number;
  payload: Record<string, unknown>;
};

export type CreateSessionInFlowSlotPayload = {
  plannedDurationMinutes: number;
  plannedOffsetMinutes?: number;
  computedDurationMinutes?: number;
  computedOffsetMinutes?: number;
  isUntimed?: boolean;
  insertBeforeSlotId?: string;
  insertAfterSlotId?: string;
};

export type CreateSessionInFlowMutationBody = {
  type: 'createSessionInFlow';
  baseVersion: number;
  payload: {
    session: {
      title: string;
      type: string;
      plannedDurationMinutes?: number;
    };
    slot: CreateSessionInFlowSlotPayload;
    startTime?: string;
    endTime?: string | null;
    eventDayId?: string;
    eventSlotId?: string;
  };
};

export type FlowMutationBody = ApplySyncMutationBody | CreateSessionInFlowMutationBody;

const MAX_CONFLICT_RETRIES = 3;

/** Keys allowed on POST applySync payload (syncFlowSchema minus id/version). Keeps body small vs spreading full FlowDto. */
const APPLY_SYNC_PAYLOAD_KEYS = [
  'parentType',
  'title',
  'description',
  'durationPlannedMinutes',
  'endOfFlowLabel',
  'eventDayId',
  'eventSlotId',
  'startTime',
  'endTime',
  'slots',
] as const;

function extractSlotContentId(content: unknown): string | undefined {
  if (content == null) return undefined;
  if (typeof content === 'string') return content;
  if (typeof content === 'object') {
    const o = content as Record<string, unknown>;
    const id = o.id ?? o._id;
    if (typeof id === 'string') return id;
    if (id != null) return String(id);
  }
  return undefined;
}

function plannedDurationMinutesFallback(slot: Record<string, unknown>): number {
  const pd = slot.plannedDurationMinutes;
  if (typeof pd === 'number') return pd;
  const c = slot.content;
  if (typeof c === 'object' && c !== null) {
    const co = c as Record<string, unknown>;
    if (typeof co.plannedDurationMinutes === 'number') return co.plannedDurationMinutes;
  }
  if (typeof slot.computedDurationMinutes === 'number') return slot.computedDurationMinutes;
  return 0;
}

function buildMinimalApplySyncSlot(slot: Record<string, unknown>): Record<string, unknown> {
  const contentType = slot.contentType;
  if (typeof contentType !== 'string') {
    throw new Error('Flow slot must include string contentType');
  }

  const out: Record<string, unknown> = {
    contentType,
    plannedDurationMinutes: plannedDurationMinutesFallback(slot),
  };

  const rawId = slot.id;
  if (typeof rawId === 'string' && rawId.length > 0) {
    if (!rawId.startsWith('optimistic-') && !rawId.startsWith('gap-')) {
      out.id = rawId;
    }
  }

  if (typeof slot.title === 'string') out.title = slot.title;

  if (contentType !== 'Gap') {
    const cid = extractSlotContentId(slot.content);
    if (cid !== undefined) out.content = cid;
  }

  if (typeof slot.plannedOffsetMinutes === 'number') out.plannedOffsetMinutes = slot.plannedOffsetMinutes;
  if (typeof slot.computedDurationMinutes === 'number') out.computedDurationMinutes = slot.computedDurationMinutes;
  if (typeof slot.computedOffsetMinutes === 'number') out.computedOffsetMinutes = slot.computedOffsetMinutes;
  if (typeof slot.isUntimed === 'boolean') out.isUntimed = slot.isUntimed;

  return out;
}

function buildMinimalApplySyncPayload(flow: Record<string, unknown>): Record<string, unknown> {
  const payload: Record<string, unknown> = {};
  for (const key of APPLY_SYNC_PAYLOAD_KEYS) {
    if (!Object.prototype.hasOwnProperty.call(flow, key)) continue;
    if (key === 'slots') {
      const slots = flow.slots;
      if (!Array.isArray(slots)) continue;
      payload.slots = slots.map((s) => {
        if (!s || typeof s !== 'object') {
          throw new Error('Flow slots must be objects');
        }
        return buildMinimalApplySyncSlot(s as Record<string, unknown>);
      });
    } else {
      payload[key] = flow[key];
    }
  }
  return payload;
}

/**
 * Minimal `flow` object for WebSocket `flowSync` (matches `syncFlowSchema` / server parse).
 * Strips `media`, `contributors`, `liveContext`, nested session DTOs in slots, etc.
 */
export function buildMinimalFlowSyncWirePayload(flow: Record<string, unknown>): Record<string, unknown> {
  const id = flow.id;
  const version = flow.version;
  if (typeof version !== 'number') {
    throw new Error('Flow document must include numeric version for flowSync');
  }
  if (typeof id !== 'string' || id.length === 0) {
    throw new Error('Flow document must include id for flowSync');
  }
  return {
    id,
    version,
    ...buildMinimalApplySyncPayload(flow),
  };
}

export function buildApplySyncBodyFromFlowDocument(flow: Record<string, unknown>): ApplySyncMutationBody {
  const wire = buildMinimalFlowSyncWirePayload(flow);
  const baseVersion = wire.version as number;
  const payload = { ...wire };
  delete payload.id;
  delete payload.version;
  return {
    type: 'applySync',
    baseVersion,
    payload,
  };
}

export async function postFlowMutation(
  flowId: string,
  body: FlowMutationBody,
  token: string,
  idempotencyKey?: string,
): Promise<FlowDto> {
  const url = buildApiUrl(`flows/${flowId}/mutations`);
  const headers: Record<string, string> = {};
  if (idempotencyKey) {
    headers['Idempotency-Key'] = idempotencyKey;
  }
  return sendJSONRequest({ url, method: METHOD.POST, body, token, headers });
}

/** @deprecated Use {@link postFlowMutation} */
export const postApplyFlowMutation = postFlowMutation;

function isConflictError(e: unknown): boolean {
  return e instanceof ServerError && e.statusCode === 409;
}

function normalizeCachedFlows(data: FlowDto | FlowDto[] | undefined): FlowDto[] {
  if (data == null) {
    return [];
  }
  return Array.isArray(data) ? data : [data];
}

function findFlowDocumentInCacheData(data: FlowDto | FlowDto[] | undefined, documentId: string): FlowDto | undefined {
  return normalizeCachedFlows(data).find((f) => f?.id === documentId);
}

/** React Query keys under which `useGetFlowQuery` stores data: [GET_FLOW_QUERY_KEY, originId, eventSlotId?]. */
function collectGetFlowQueryKeysContainingDocumentId(queryClient: QueryClient, documentId: string): QueryKey[] {
  const keys: QueryKey[] = [];
  for (const [key, data] of queryClient.getQueriesData<FlowDto | FlowDto[]>({ queryKey: [GET_FLOW_QUERY_KEY] })) {
    if (findFlowDocumentInCacheData(data, documentId)) {
      keys.push(key);
    }
  }
  return keys;
}

/**
 * After a 409, refetch the flow cache entry that holds the mutated mode document (PLAN id ≠ origin id).
 */
async function refetchCachedFlowForConflictRetry(
  queryClient: QueryClient,
  mutationDocumentId: string,
  explicitFlowQueryKey?: QueryKey,
): Promise<FlowDto | undefined> {
  if (explicitFlowQueryKey != null && explicitFlowQueryKey.length >= 2) {
    await queryClient.refetchQueries({ queryKey: explicitFlowQueryKey });
    const data = queryClient.getQueryData<FlowDto | FlowDto[]>(explicitFlowQueryKey);
    const fromExplicit = findFlowDocumentInCacheData(data, mutationDocumentId);
    if (fromExplicit != null && typeof fromExplicit.version === 'number') {
      return fromExplicit;
    }
  }

  const keysToRefetch = collectGetFlowQueryKeysContainingDocumentId(queryClient, mutationDocumentId);
  if (keysToRefetch.length > 0) {
    await Promise.all(keysToRefetch.map((qKey) => queryClient.refetchQueries({ queryKey: qKey })));
  } else {
    await queryClient.refetchQueries({ queryKey: [GET_FLOW_QUERY_KEY, mutationDocumentId] });
  }

  for (const [, data] of queryClient.getQueriesData<FlowDto | FlowDto[]>({ queryKey: [GET_FLOW_QUERY_KEY] })) {
    const doc = findFlowDocumentInCacheData(data, mutationDocumentId);
    if (doc != null && typeof doc.version === 'number') {
      return doc;
    }
  }
  return undefined;
}

/**
 * POST applySync with silent 409 handling: refetch flow query, rebuild body with `mergeWithServerFlow`, retry.
 */
export async function postFlowMutationWithConflictRetry(options: {
  flowId: string;
  token: string;
  queryClient: QueryClient;
  /** Build request body; after refetch, receives latest server flow (or undefined on first attempt). */
  buildBody: (serverFlow: FlowDto | undefined) => FlowMutationBody;
  idempotencyKey?: string;
  /**
   * When set, refetch this key first (same shape as `useGetFlowQuery`: [GET_FLOW_QUERY_KEY, originId, eventSlotId?]).
   * `flowId` remains the mode document id used in POST `/flows/:flowId/mutations`.
   */
  flowQueryKey?: QueryKey;
}): Promise<FlowDto> {
  const { flowId, token, queryClient, buildBody, idempotencyKey, flowQueryKey } = options;
  let lastError: unknown;

  for (let attempt = 0; attempt < MAX_CONFLICT_RETRIES; attempt += 1) {
    let serverFlow: FlowDto | undefined;
    if (attempt > 0) {
      serverFlow = await refetchCachedFlowForConflictRetry(queryClient, flowId, flowQueryKey);
    }

    const body = buildBody(serverFlow);
    try {
      return await postFlowMutation(flowId, body, token, attempt === 0 ? idempotencyKey : undefined);
    } catch (e) {
      lastError = e;
      if (isConflictError(e) && attempt < MAX_CONFLICT_RETRIES - 1) {
        continue;
      }
      throw e;
    }
  }

  throw lastError instanceof Error ? lastError : new Error('Flow mutation failed');
}

/** @deprecated Use {@link postFlowMutationWithConflictRetry} */
export const postApplyFlowMutationWithConflictRetry = postFlowMutationWithConflictRetry;
